import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import type { SchemaOrgMarkup } from '../types/schema';

interface SEOProps {
  title: string;
  description: string;
  path: string;
  image?: string;
  schema?: SchemaOrgMarkup | SchemaOrgMarkup[];
  noindex?: boolean;
  noCanonical?: boolean;
}

const SEO: React.FC<SEOProps> = ({ title, description, path, image, schema, noindex, noCanonical }) => {
  const url = `https://robertgarza.us${path}`;
  const defaultImage = 'https://robertgarza.us/social-share.png';

  // Client-side validation warnings (T017)
  useEffect(() => {
    if (title.length > 60) {
      console.warn(
        `SEO Warning: Title exceeds 60 characters (${title.length} chars): "${title}"\n` +
          `Consider shortening to 50-60 characters for optimal search result display.`,
      );
    }
    if (title.length < 50) {
      console.warn(
        `SEO Warning: Title is less than 50 characters (${title.length} chars): "${title}"\n` +
          `Consider expanding to 50-60 characters for better keyword coverage.`,
      );
    }
    if (description.length > 160) {
      console.warn(
        `SEO Warning: Meta description exceeds 160 characters (${description.length} chars)\n` +
          `Consider shortening to 150-160 characters for optimal search result display.`,
      );
    }
    if (description.length < 150) {
      console.warn(
        `SEO Warning: Meta description is less than 150 characters (${description.length} chars)\n` +
          `Consider expanding to 150-160 characters for better search result display.`,
      );
    }
  }, [title, description]);

  return (
    <Helmet>
      <title>{title}</title>
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta name="description" content={description} />
      {!noCanonical && <link rel="canonical" href={url} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image || defaultImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image || defaultImage} />

      {/* Schema.org JSON-LD (T016) */}
      {schema && <script type="application/ld+json">{JSON.stringify(Array.isArray(schema) ? schema : schema)}</script>}
    </Helmet>
  );
};

export default SEO;
